import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { Endpoints } from '../../core/resources/endpoints';
import { environment } from '../../../environments/environment';
import { SatelliteLock } from '../../core/interfaces/satelliteLock';
import { SatelliteLockType } from '../../core/interfaces/satelliteLockType';
import { PaginationList } from '../../core/models/pagination-list';
import { FilterModel } from '../../core/models/filter';
import { Filter } from '../../core/models/filter';

interface SatelliteLockListResponse {
  items: SatelliteLock[];
  nextPageKey?: string;
  totalItems?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SatelliteLockService {
  public paginationList: PaginationList = new PaginationList();
  public readonly pathImageSatelliteLock = `satellite-lock/%s`;

  constructor(
    private readonly http: HttpClient,
    private readonly endpointResources: Endpoints,
  ) { }

  getSatelliteLocks(pageSize: number, nextPageKey?: string, filters?: string): Observable<SatelliteLockListResponse> {
    const params = this.getParamsFormatted(filters, nextPageKey, pageSize);
    return this.http.get<SatelliteLockListResponse>(
      `${environment.urlServerTeclogi}${this.endpointResources.satelliteLock}`,
      { params }
    );
  }

  getSatelliteLocksItems(pageSize: number, nextPageKey?: string, filters?: string): Observable<SatelliteLock[]> {
    return this.getSatelliteLocks(pageSize, nextPageKey, filters).pipe(
      map((response: SatelliteLockListResponse) => {
        return response.items || [];
      })
    );
  }


  getSatelliteLocksByTypeName(typeName: string, pageSize?: number): Observable<SatelliteLock[]> {
    let params = new HttpParams();
    if (typeName) params = params.set('typeName', typeName);
    if (pageSize) params = params.set('pageSize', pageSize.toString());
    return this.http.get<SatelliteLockListResponse>(
      `${environment.urlServerTeclogi}${this.endpointResources.satelliteLock}`,
      { params }
    ).pipe(
      map((response: SatelliteLockListResponse) => {
        return response.items || [];
      })
    );
  }

  getSatelliteLocksLegacy(pageKey: number, pageSize: number, filters?: string): Observable<SatelliteLock[]> {
    return this.getSatelliteLocksItems(pageSize, undefined, filters);
  }


  private getParamsFormatted(paramsFilter?: string, nextPageKey?: string, pageSize?: number): HttpParams {
    let params = new HttpParams();
    if (paramsFilter) {
      paramsFilter.split('&').forEach(param => {
        if (param) {
          const [key, value] = param.split('=');
          if (key && value && ['id', 'typeName', 'stateDevice', 'imei', 'documentId'].includes(key)) {
            const decodedValue = decodeURIComponent(value);
            if (decodedValue !== null && decodedValue !== undefined && decodedValue !== '') {
              params = params.append(key, decodedValue);
            }
          }
        }
      });
    }
    if (pageSize) params = params.append('pageSize', pageSize.toString());
    if (nextPageKey) params = params.append('pageKey', nextPageKey);
    return params;
  }


  getSatelliteLockByImei(imei: string): Observable<SatelliteLock> {
    return this.http.get<SatelliteLock>(
      `${environment.urlServerTeclogi}${this.endpointResources.satelliteLock}/${imei}`
    );
  }

  createSatelliteLock(satelliteLock: SatelliteLock): Observable<SatelliteLock> {
    return this.http.post<SatelliteLock>(
      environment.urlServerTeclogi + this.endpointResources.satelliteLock,
      satelliteLock
    );
  }

  updateSatelliteLock(satelliteLock: SatelliteLock): Observable<SatelliteLock> {
    return this.http.put<SatelliteLock>(
      environment.urlServerTeclogi + this.endpointResources.satelliteLock,
      satelliteLock
    );
  }


  deleteSatelliteLock(imei: string): Observable<any> {
    return this.http.delete(
      `${environment.urlServerTeclogi}${this.endpointResources.satelliteLock}/${imei}`
    );
  }


  getSatelliteLockTypes(): Observable<{ catalog: SatelliteLockType[] }> {
    return this.http.get<{ catalog: SatelliteLockType[] }>(
      environment.urlServerTeclogi + this.endpointResources.getSatelliteLockTypes
    );
  }

  public resetPagination(): void {
    this.paginationList.setList([]);
  }


  public getOptionsFilter(): FilterModel[] {
    return [
      {
        filterName: Filter.showFilterSatelliteLockId,
        validations: true,
        controls: { id: new FormControl('') },
        type: 'input',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterSatelliteLockType,
        validations: true,
        controls: { typeName: new FormControl('') },
        type: 'select',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterSatelliteLockStateDevice,
        validations: true,
        controls: { stateDevice: new FormControl('') },
        type: 'select',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterSatelliteLockIMEI,
        validations: true,
        controls: { imei: new FormControl('') },
        type: 'input',
        typeFilter: 'dynamic'
      }
    ];
  }
}

